import LanguageManager from "../../LanguageManager.js";
import LocalStats from "../../serverConnection/LocalStats.js";

/**
 * @typedef {object} MatchData
 * @property {string} date - The match date
 * @property {string} time - The match time
 * @property {string} playerImage - The player image URL
 * @property {string} playerName - The player name
 * @property {string} OpponentImage - The opponent image URL
 * @property {string} OpponentName - The opponent name
 * @property {string} score - The match score
 * @property {string} duration - The match duration
 * @property {string} [totalBounces] - The total bounces (only for pong game)
 * @property {string} [goalGap] - The goal gap (only for pong game)
 * @property {string} type - The game type (pong or tetris)
 * @property {string} [nbrOfTetris] - The number of tetris (only for tetris game)
 * @property {string} [nbrOfLinesCleared] - The number of lines cleared (only for tetris game)
 */

class MatchView {
    #cardTemplate = null
    #updateHistoryBind = null

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Constructor for the MatchView class. This is a public deployment:
     * match history is built locally from the game's own events (result/forfeit) and
     * kept in LocalStats (localStorage), never on a server, so there is no websocket
     * push to wait for any more.
     */
    constructor() {
        const mutationObserver = new MutationObserver((record, observer) => {
            const root = document.getElementById('matchTabCenterMenu')
            if (!root) {
                return;
            }
            observer.disconnect()
        })
        mutationObserver.observe(document.body, { childList: true, subtree: true })

        this.#updateHistoryBind = this.updateHistory.bind(this)
        window.addEventListener('localStatsUpdated', this.#updateHistoryBind)
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Pull the local match history, format it and update the view. Used
     * both as the 'localStatsUpdated' handler and as a manual refresh when the tab opens.
     * @returns {Promise<void>}
     */
    async updateHistory() {
        const array = LocalStats.getMatchHistory().map(this.#formatMatchData)
        await this.injectArrayOfMatchCard(array, true)
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * Edited by: Corentin (ccharton) Charton
     * @description Format raw match data from backend into a frontend usable format
     * @param matchData {object} - The raw match data from backend
     * @returns {{date: string, time: string, playerImage: string, playerName: string, OpponentImage: string, OpponentName: string, score: string, duration: string, totalBounces: string, goalGap: string, nbrOfTetris: string, nbrOfLinesCleared: string, type: string}} - The formatted match data
     */
    #formatMatchData(matchData) {
        const paddding = (numbers) => {
            return String(numbers).padStart(2, "0")
        }

        const date = new Date(matchData.game_time)
        const formatedDate = `${paddding(date.getDate())}/${paddding(date.getMonth() + 1)}/${date.getFullYear()}`
        const time = `${paddding(date.getHours())}:${paddding(date.getMinutes())}`
        const duration = `${Math.floor(matchData.duration_seconds / 60)}:${Math.round(matchData.duration_seconds % 60)}`
        return {
            date              : formatedDate,
            time              : time,
            playerImage       : matchData.user_profile_picture,
            playerName        : matchData.user_name,
            OpponentImage     : matchData.opponent_profile_picture,
            OpponentName      : matchData.opponent_name,
            score             : `${matchData.user_score}-${matchData.opponent_score}`,
            duration          : duration,
            totalBounces      : String(matchData.ball_bounces),
            goalGap           : matchData.avg_time_to_score,
            nbrOfTetris       : matchData.tetrises_cleared,
            nbrOfLinesCleared : matchData.lines_cleared,
            type              : matchData.game_type
        }
    }
    

    /**
     * @author Corentin (ccharton) Charton
     * @description Load the match card template
     * @returns {Promise<void>}
     */
    async loadCardTemplate() {
        try {
            const template = await fetch("./overlay/components/centerMenuTab/template/matchCard.html")
            if (!template.ok) {
                throw new Error("Template not found")
            }
            const templateText = await template.text()

            const tempDiv = document.createElement("div")
            tempDiv.innerHTML = templateText

            this.#cardTemplate = Array.from(tempDiv.children).find((elem) => elem.tagName !== "SCRIPT")
        } catch (error) {
            console.error("Error loading card template:", error)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Clear the match container
     * @returns {Promise<void>}
     */
    async clearMatchContainer() {
        const matchContainer = document.getElementById("matchContainer")
        if (!matchContainer) {
            console.error("Match container not found")
            return
        }
        while (matchContainer.firstChild) {
            matchContainer.removeChild(matchContainer.firstChild)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Inject an array of match cards into the match container
     * @param arrayOfMatch {MatchData[]} - The array of match data (every match is an object) to inject
     * @param needClear {Boolean} - If true, the match container will be cleared before injecting the new matches
     * @returns {Promise<void>}
     */
    async injectArrayOfMatchCard(arrayOfMatch, needClear = true) {
        if (!this.#cardTemplate) {
            await this.loadCardTemplate()
        }

        const matchContainer = document.getElementById("matchContainer")
        if (!matchContainer) {
            return
        }

        if (needClear) {
            await this.clearMatchContainer()
        }

        const virtualDOM = document.createDocumentFragment()

        for (let match of arrayOfMatch) {
            const matchCard = this.#cardTemplate.cloneNode(true)

            matchCard.querySelector('#matchDate').textContent = match.date
            matchCard.querySelector('#matchTime').textContent = match.time
            matchCard.querySelector('#playerImage').src = match.playerImage
            matchCard.querySelector('#playerName').textContent = match.playerName
            matchCard.querySelector('#matchScore').textContent = match.score
            matchCard.querySelector('#OpponentImage').src = match.OpponentImage
            matchCard.querySelector('#OpponentName').textContent = match.OpponentName

            const matchStatValueArray = matchCard.querySelectorAll('.matchStatValue')
            const matchStatTitle = matchCard.querySelectorAll('.matchStatTitle')

            if (match.type === 'pong') {
                matchStatValueArray[0].textContent = match.duration
                matchStatValueArray[1].textContent = match.totalBounces
                matchStatValueArray[2].textContent = match.goalGap
            } else if (match.type === 'tetris') {
                matchStatTitle[0].dataset.i18n = 'matchDuration'
                matchStatValueArray[0].textContent = match.duration

                matchStatTitle[1].dataset.i18n = 'matchTotalTetris'
                matchStatValueArray[1].textContent = match.nbrOfTetris

                matchStatTitle[2].dataset.i18n = 'matchTotalLinesCleared'
                matchStatValueArray[2].textContent = match.nbrOfLinesCleared
            }

            virtualDOM.appendChild(matchCard)
        }
        matchContainer.appendChild(virtualDOM)
        LanguageManager.updateContent()
    }
}

export default new MatchView()